import { createTheme } from '@mui/material';
import type { ColorMode } from '../config/themeMode';
import DesignSystem, { applyColorMode } from '../designSystem';

export function createAppTheme(mode: ColorMode) {
  applyColorMode(mode);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: DesignSystem.colors.primary,
        light: DesignSystem.colors.primaryLight,
        dark: DesignSystem.colors.primaryDark,
      },
      secondary: {
        main: DesignSystem.colors.primary,
        light: DesignSystem.colors.primaryLight,
        dark: DesignSystem.colors.primaryDark,
      },
      success: { main: DesignSystem.colors.success },
      warning: { main: DesignSystem.colors.warning },
      error: { main: DesignSystem.colors.error },
      background: {
        default: DesignSystem.colors.canvas,
        paper: DesignSystem.colors.surface,
      },
      divider: DesignSystem.colors.separator,
      text: {
        primary: DesignSystem.colors.textPrimary,
        secondary: DesignSystem.colors.textSecondary,
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"SF Pro Text"',
        '"Segoe UI"',
        'system-ui',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: DesignSystem.typography.h1.fontSize,
        fontWeight: DesignSystem.typography.h1.fontWeight,
        lineHeight: DesignSystem.typography.h1.lineHeight,
        letterSpacing: '-0.01em',
      },
      h2: {
        fontSize: DesignSystem.typography.h2.fontSize,
        fontWeight: DesignSystem.typography.h2.fontWeight,
        lineHeight: DesignSystem.typography.h2.lineHeight,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: DesignSystem.typography.h3.fontSize,
        fontWeight: DesignSystem.typography.h3.fontWeight,
        lineHeight: DesignSystem.typography.h3.lineHeight,
      },
      h4: {
        fontSize: DesignSystem.typography.displayMedium.fontSize,
        fontWeight: DesignSystem.typography.displayMedium.fontWeight,
        lineHeight: DesignSystem.typography.displayMedium.lineHeight,
        letterSpacing: '-0.02em',
      },
      h5: {
        fontSize: DesignSystem.typography.h2.fontSize,
        fontWeight: DesignSystem.typography.h2.fontWeight,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontSize: DesignSystem.typography.h3.fontSize,
        fontWeight: DesignSystem.typography.h3.fontWeight,
        letterSpacing: '-0.01em',
      },
      subtitle1: {
        fontSize: DesignSystem.typography.label.fontSize,
        fontWeight: DesignSystem.typography.label.fontWeight,
        lineHeight: DesignSystem.typography.label.lineHeight,
      },
      subtitle2: {
        fontSize: DesignSystem.typography.labelSmall.fontSize,
        fontWeight: DesignSystem.typography.labelSmall.fontWeight,
        lineHeight: DesignSystem.typography.labelSmall.lineHeight,
      },
      body1: {
        fontSize: DesignSystem.typography.body.fontSize,
        fontWeight: DesignSystem.typography.body.fontWeight,
        lineHeight: DesignSystem.typography.body.lineHeight,
      },
      body2: {
        fontSize: DesignSystem.typography.bodySmall.fontSize,
        fontWeight: DesignSystem.typography.bodySmall.fontWeight,
        lineHeight: DesignSystem.typography.bodySmall.lineHeight,
      },
      button: {
        fontSize: DesignSystem.typography.button.fontSize,
        fontWeight: DesignSystem.typography.button.fontWeight,
        lineHeight: DesignSystem.typography.button.lineHeight,
        textTransform: 'none',
      },
      caption: {
        fontSize: DesignSystem.typography.caption.fontSize,
        fontWeight: DesignSystem.typography.caption.fontWeight,
        lineHeight: DesignSystem.typography.caption.lineHeight,
      },
    },
    shape: {
      borderRadius: parseInt(DesignSystem.borderRadius.md, 10),
    },
    spacing: 4,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            backgroundColor: DesignSystem.colors.canvas,
            color: DesignSystem.colors.textPrimary,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-track': {
              background: DesignSystem.colors.surfaceElevated,
            },
            '&::-webkit-scrollbar-thumb': {
              background: DesignSystem.colors.border,
              borderRadius: DesignSystem.borderRadius.sm,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: DesignSystem.colors.textSecondary,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: DesignSystem.colors.surfaceElevated,
            borderRadius: DesignSystem.borderRadius.md,
            border: `1px solid ${DesignSystem.colors.border}`,
            boxShadow: DesignSystem.shadows.none,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: DesignSystem.borderRadius.xl,
            fontWeight: DesignSystem.typography.button.fontWeight,
            fontSize: DesignSystem.typography.button.fontSize,
            padding: `${DesignSystem.padding.button.paddingTop} ${DesignSystem.padding.button.paddingRight}`,
            transition: `all ${DesignSystem.animation.normal} ${DesignSystem.animation.easing}`,
            '&:hover': { transform: 'translateY(-1px)' },
            '&:active': { transform: 'translateY(0)' },
          },
          contained: {
            backgroundColor: DesignSystem.colors.action,
            color: DesignSystem.colors.onPrimary,
            '&:hover': { backgroundColor: DesignSystem.colors.actionDark },
          },
          outlined: {
            borderWidth: '1.5px',
            borderColor: DesignSystem.colors.primary,
            color: DesignSystem.colors.primary,
            backgroundColor: 'transparent',
            '&:hover': {
              borderColor: DesignSystem.colors.primaryDark,
              backgroundColor: DesignSystem.colors.primaryLight,
              borderWidth: '1.5px',
            },
          },
          text: {
            color: DesignSystem.colors.primary,
            '&:hover': { backgroundColor: DesignSystem.colors.primaryLight },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: DesignSystem.borderRadius.md,
            backgroundColor: DesignSystem.colors.surfaceElevated,
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            '@media (min-width: 1200px)': { maxWidth: 1200 },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { border: `2px solid ${DesignSystem.colors.surface}` },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: DesignSystem.borderRadius.pill,
            fontWeight: DesignSystem.typography.labelSmall.fontWeight,
            fontSize: DesignSystem.typography.labelSmall.fontSize,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: DesignSystem.colors.textSecondary,
            '&.Mui-focused': { color: DesignSystem.colors.primary },
            '&.Mui-disabled': { color: DesignSystem.colors.textTertiary },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { color: DesignSystem.colors.textSecondary },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: DesignSystem.borderRadius.md,
            backgroundColor: DesignSystem.colors.canvas,
            color: DesignSystem.colors.textPrimary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: DesignSystem.colors.separator,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: DesignSystem.colors.border,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: DesignSystem.colors.primary,
            },
          },
          input: {
            color: DesignSystem.colors.textPrimary,
            '&::placeholder': {
              color: DesignSystem.colors.textSecondary,
              opacity: 1,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: DesignSystem.colors.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: DesignSystem.colors.textSecondary,
            '&.Mui-selected': { color: DesignSystem.colors.textPrimary },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: DesignSystem.colors.surface,
            backgroundImage: 'none',
            borderRadius: DesignSystem.borderRadius.lg,
            border: `1px solid ${DesignSystem.colors.separator}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: DesignSystem.borderRadius.md },
        },
      },
    },
  });
}
